import React from 'react'
import { debounce } from 'lodash'
import { Tag } from 'iconsax-react'
import { useTheme } from '@mui/material'

import TagInputV2 from '../../../TagInputv2'
import TagV2 from './TagV2'

import { useEditContext } from '@SonarCX/providers/EditProvider'
import { useCustomerContext } from '@SonarCX/providers/CustomerProvider'
import useStyles from './styles'

type TagsSectionProps = {
  readOnly?: boolean
  canEdit?: boolean
  modal?: boolean
}

const TagsSection: React.FC<TagsSectionProps> = ({
  readOnly,
  canEdit,
  modal
}) => {
  const { values, handleChange, handleUpdateUser } = useCustomerContext()
  const { setEditedMeta } = useEditContext()
  const { tags = [] } = values
  const theme = useTheme()

  const [tagEdited, setTagEdited] = React.useState(false)

  const classes = useStyles({ modal })

  // eslint-disable-next-line
  const delayedSave = React.useCallback(
    debounce(handleUpdateUser, 1000, { leading: true }),
    [handleUpdateUser]
  )

  React.useEffect(() => {
    if (tagEdited) {
      delayedSave()
      setTagEdited(false)
    }
  }, [tagEdited, delayedSave])

  const submitTag = React.useCallback(
    tag => {
      const trimmed = tag.trim()

      if (trimmed.length) {
        handleChange(trimmed, 'tags')
        setEditedMeta(true)
        setTagEdited(true)
      }
    },
    [handleChange, setEditedMeta, setTagEdited]
  )

  const deleteTag = React.useCallback(
    i => {
      if (canEdit) {
        handleChange(-1, 'tags', i)
        setEditedMeta(true)
        setTagEdited(true)
      }
    },
    [canEdit, handleChange, setEditedMeta, setTagEdited]
  )

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        <Tag
          size={theme.size.base}
          variant='Bold'
          color='grey'
          style={{ marginRight: 9 }}
        />
        TAGS
      </div>

      {!readOnly && (
        // @ts-ignore
        <TagInputV2
          height={24}
          inputClass={classes.inputClass}
          handleAddTag={submitTag}
          alwaysShowInput
          noAutofocus
          customPlaceholder
          onResetValueTrigger={values.id}
          className={classes.inputRoot}
          disabled={!canEdit}
        />
      )}

      {tags.length === 0 ? (
        <div className={classes.noTags}>No Tags</div>
      ) : (
        <div className={classes.tagList}>
          {tags.map((tag, i) => (
            <TagV2
              key={i}
              tag={tag}
              onDelete={() => deleteTag(i)}
              disabled={!canEdit || readOnly}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default TagsSection
